import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SharePanel from "@/components/feature/SharePanel";
import WorkCard from "@/components/feature/WorkCard";
import { formatScore, getCharactersByWork, getRelatedWorks, getWorkById } from "@/lib/content";
import useDocumentTitle from "@/hooks/useDocumentTitle";

export default function Detail() {
  const { id } = useParams();
  const work = getWorkById(id);

  useDocumentTitle(
    work ? `${work.title}剧情介绍与角色分析 - 亲吻姐姐` : "影视作品不存在 - 亲吻姐姐",
    work
      ? `《${work.title}》（${work.type}·${work.year}）剧情概要：${work.synopsis} 本文提供分段剧情介绍与主要角色分析。`
      : "该影视作品可能已下架或链接有误，请返回首页浏览更多影视资讯。"
  );

  if (!work) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-4xl"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            影视作品不存在或已下架
          </h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            你访问的作品可能已被移除，或链接地址不正确。可以回到首页重新浏览热门影视资讯与剧情解析。
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
            >
              浏览全部作品
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const characters = getCharactersByWork(work.id);
  const related = getRelatedWorks(work, 3);

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-12 lg:px-8">
          <Breadcrumb items={[{ label: "影视分类", to: "/category" }, { label: work.title }]} />
          <div className="mt-6 flex flex-col gap-8 lg:flex-row">
            <div className="w-full shrink-0 sm:w-64 lg:w-72">
              <div className="aspect-[3/4] w-full overflow-hidden rounded-xl border border-background-200 bg-background-200">
                <img
                  src={work.poster}
                  alt={`${work.title} 剧照海报`}
                  title={`${work.title} 影视剧剧情介绍与角色分析`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">{work.type}</span>
                <span className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-700">{work.region}</span>
                <span className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-700">{work.year}</span>
              </div>
              <h1 className="mt-4 font-heading text-3xl font-extrabold text-foreground-950 md:text-4xl">{work.title}</h1>
              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
                <span className="flex items-baseline gap-1">
                  <span className="font-heading text-3xl font-extrabold text-accent-600">{formatScore(work.rating)}</span>
                  <span className="text-sm text-foreground-500">综合评分</span>
                </span>
                <span className="text-sm text-foreground-600">{work.genres.join(" / ")}</span>
              </div>

              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">{work.synopsis}</p>

              <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
                <div className="flex gap-2">
                  <dt className="shrink-0 text-foreground-500">导演</dt>
                  <dd className="text-foreground-800">{work.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-foreground-500">片长 / 集数</dt>
                  <dd className="text-foreground-800">{work.duration}</dd>
                </div>
                <div className="flex gap-2 sm:col-span-2">
                  <dt className="shrink-0 text-foreground-500">主演</dt>
                  <dd className="text-foreground-800">{work.cast.join("、")}</dd>
                </div>
                <div className="flex gap-2 sm:col-span-2">
                  <dt className="shrink-0 text-foreground-500">类型标签</dt>
                  <dd className="flex flex-wrap gap-2">
                    {work.genres.map((genre) => (
                      <span key={genre} className="rounded-md bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-800">
                        {genre}
                      </span>
                    ))}
                  </dd>
                </div>
              </dl>

              <div className="mt-7 flex flex-wrap gap-3">
                <SharePanel title={`《${work.title}》剧情介绍与角色分析 - 亲吻姐姐`} />
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
                >
                  <i className="ri-layout-grid-line"></i>
                  更多同类作品
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <article className="lg:col-span-2">
            <h2 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-foreground-950">
              <i className="ri-book-open-line text-primary-600"></i>
              剧情介绍
            </h2>
            <div className="mt-5 space-y-5">
              {work.plot.map((paragraph, index) => (
                <p key={index} className="text-sm leading-loose text-foreground-700 md:text-base">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 rounded-xl border border-background-200 bg-background-100 p-5 md:p-6">
              <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-lightbulb-line text-accent-600"></i>
                剧情解析要点
              </h3>
              <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-700">
                <li className="flex gap-2.5">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
                  人物关系是推动剧情的关键：主要角色的每一次选择，都对应着一段未被说出口的动机。
                </li>
                <li className="flex gap-2.5">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
                  叙事结构服务于主题：注意分段之间的呼应，它们往往暗示了作品的真正落点。
                </li>
                <li className="flex gap-2.5">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
                  结局并非终点：真正的答案通常藏在角色的成长与和解之中。
                </li>
              </ul>
            </div>
          </article>

          <aside className="lg:col-span-1">
            <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950">
              <i className="ri-group-line text-primary-600"></i>
              主要角色
            </h2>
            <div className="mt-5 flex flex-col gap-4">
              {characters.map((character) => (
                <Link
                  key={character.id}
                  to={`/character/${character.id}`}
                  className="group flex items-center gap-4 rounded-xl border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 hover:bg-background-100"
                >
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-background-200">
                    <img
                      src={character.image}
                      alt={`${character.name} 角色形象`}
                      title={`${character.name} 角色分析`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-700">
                      {character.name}
                    </p>
                    <p className="truncate text-xs text-foreground-500">{character.identity}</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-xl text-foreground-400 transition-transform group-hover:translate-x-0.5 group-hover:text-primary-600"></i>
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-background-100">
          <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 lg:px-8">
            <h2 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-foreground-950">
              <i className="ri-magic-line text-accent-600"></i>
              相关推荐
            </h2>
            <p className="mt-2 text-sm text-foreground-600">根据类型、导演与题材标签为您匹配。</p>
            <div className="mt-7 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <WorkCard key={item.id} work={item} />
              ))}
            </div>
          </div>
        </section>
      )}
    </SiteLayout>
  );
}